import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useEffect } from 'react';
import { OfferingType } from 'stores/networkInterfaces';
import { getEnumObject } from 'utils/enumUtils';
import { Envelope, IAggBucket, IAggField, IFilter, ILocationFilter } from 'utils/globalTypes';
import { isNumeric } from 'utils/object';
import { getSingleValue } from 'utils/urlUtils';

export const PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;
export const DATE_RANGE_FILTER_KEY = 'date_range';

export interface QueryParams {
  term?: string;
  userTerm?: string;
  filters?: IFilter[];
  sortField?: string;
  sortDesc?: boolean;
}

export const FILTERS = {
  asset_type: {
    filterType: 'Property Type',
    keyName: 'asset_type',
    filterValues: []
  },
  offering_status: {
    filterType: 'Offering Status',
    keyName: 'offering_status',
    filterValues: []
  },
  offering_type: {
    filterType: 'Offering Type',
    keyName: 'offering_type',
    filterValues: []
  },
  sponsor_id: {
    filterType: 'Sponsors',
    keyName: 'sponsor_id',
    filterValues: []
  },
  user_sponsor_id: {
    filterType: 'Sponsors',
    keyName: 'sponsor_id',
    noneValue: true,
    filterValues: []
  },
  t12_yield: {
    filterType: 'Cash Yield',
    keyName: 't12_yield',
    filterValues: []
  },
  implied_cap_rate: {
    filterType: 'Implied Cap',
    keyName: 'implied_cap_rate',
    filterValues: []
  }
};

export const AGGREGATIONS = {
  asset_type: {
    field: 'asset_type',
    type: 'value'
  } as IAggField,
  offering_type: {
    field: 'offering_type',
    type: 'value',
    names: getEnumObject(OfferingType)
  } as IAggField,
  sponsor_id: {
    field: 'sponsor_id',
    type: 'value',
    name_field: 'sponsor_name'
  } as IAggField,
  t12_yield: {
    field: 't12_yield',
    type: 'range',
    ranges: [0.04, 0.06, 0.08]
  } as IAggField,
  implied_cap_rate: {
    field: 'implied_cap_rate',
    type: 'range',
    ranges: [0.04, 0.06]
  } as IAggField,
  posted_at: {
    field: 'posted_at',
    type: 'date'
  } as IAggField,
  changed_at: {
    field: 'changed_at',
    type: 'date'
  } as IAggField,
  updated_at: {
    field: 'updated_at',
    type: 'date'
  } as IAggField
};

export const getRangeFilter = (filterName: string, fieldName: string, values: number[]) => {
  return {
    filterType: filterName,
    keyName: fieldName,
    filterValues: values.reduce((acc: IAggBucket[], cur, i) => {
      if (i === 0) {
        acc.push({ key: `:${cur}`, to: cur });
      } else if (i === values.length - 1) {
        acc.push({
          key: `${values[i - 1]}:${cur}`,
          from: values[i - 1],
          to: cur
        });
        acc.push({ key: `${cur}:`, from: cur });
      } else {
        acc.push({
          key: `${values[i - 1]}:${cur}`,
          from: values[i - 1],
          to: cur
        });
      }
      return acc;
    }, [])
  };
};

export const getDateFilter = (filterName: string, fieldName: string) => {
  return {
    filterType: filterName,
    keyName: fieldName,
    filterValues: [
      { name: 'This Month', key: 'this_month' },
      { name: 'Last Month', key: 'last_month' },
      { name: 'Last 6 Months', key: 'last_6_months' },
      { name: 'Last Year', key: 'last_year' }
      // { name: 'Custom', key: DATE_RANGE_FILTER_KEY }
    ]
  };
};

export const getDemoUsersFilter = (filterType = 'Demo', keyName = 'creator_id') => {
  return {
    filterType: filterType,
    keyName: keyName,
    filterValues: [
      { name: 'Regular Users', key: '' },
      { name: 'Demo Users', key: '*' }
    ]
  };
};

export const getDemoOfferingsFilter = (filterType = 'Demo', keyName = 'creator_id') => {
  return {
    filterType: filterType,
    keyName: keyName,
    filterValues: [
      { name: 'Regular Offerings', key: '' },
      { name: 'Demo Offerings', key: '*' }
    ]
  };
};

interface Enum {
  [id: number]: string;
}

export const getEnumValueFilter = (
  filterName: string,
  fieldName: string,
  values: Enum,
  keyMapper: (value: number) => string | undefined
) => {
  return {
    filterType: filterName,
    keyName: fieldName,
    filterValues: values
      ? Object.entries(values)
          .filter(([key]) => isNumeric(key))
          .map(([key]) => ({
            key: key,
            name: keyMapper(Number(key))
          }))
      : undefined
  };
};

export const getDateQueryFilter = (fieldName: string, values?: string[]) => {
  if (!values) return undefined;
  let filter = '';
  let currentTimestamp = 0;
  const currentDate = new Date();
  for (const value of values) {
    if (value == 'this_month') {
      const timestamp = new Date().setDate(1);
      currentTimestamp = currentTimestamp ? Math.min(currentTimestamp, timestamp) : timestamp;
    } else if (value == 'last_month') {
      const timestamp = new Date().setMonth(currentDate.getMonth() - 1);
      currentTimestamp = currentTimestamp ? Math.min(currentTimestamp, timestamp) : timestamp;
    } else if (value == 'last_6_months') {
      const timestamp = new Date().setMonth(currentDate.getMonth() - 6);
      currentTimestamp = currentTimestamp ? Math.min(currentTimestamp, timestamp) : timestamp;
    } else if (value == 'last_year') {
      const timestamp = new Date().setFullYear(currentDate.getFullYear() - 1);
      currentTimestamp = currentTimestamp ? Math.min(currentTimestamp, timestamp) : timestamp;
    } else {
      filter = value;
      break;
    }
    if (currentTimestamp) {
      filter = `${currentTimestamp}:`;
    }
  }

  return filter ? { [fieldName]: filter } : undefined;
};

export const getRangeQueryFilter = (fieldName: string, value?: string[]) => {
  if (!value) return undefined;
  return { [fieldName]: value[0] };
};

interface IBuildQuery {
  sortDirection?: string;
  sortField?: string;
  aggregations?: string[];
  from?: number;
  size?: number;
  searchTerm?: string;
  filters?: IFilter[];
  locationFilter?: ILocationFilter;
}

export const defaultQuery = {
  sortField: 'changed_at',
  sortDirection: 'desc',
  resultsPerPage: MAX_PAGE_SIZE,
  aggregations: AGGREGATIONS
};

export const buildSearchSort = (sortDirection?: string, sortField?: string) => {
  if (sortDirection && sortField) {
    const sortFieldSplit = sortField.split('.');
    if (sortFieldSplit.length > 1) {
      return [
        {
          [`${sortField}`]: {
            nested_path: sortFieldSplit[0],
            order: sortDirection
          }
        }
      ];
    } else {
      return [
        {
          [`${sortField}`]: {
            order: sortDirection
          }
        }
      ];
    }
  }
};

export const buildSearchMatch = (searchTerm?: string): Record<string, unknown> => {
  return searchTerm
    ? {
        bool: {
          minimum_should_match: 1,
          should: [
            {
              match: {
                offering_id: {
                  query: searchTerm,
                  operator: 'AND',
                  boost: 5
                }
              }
            },
            {
              match: {
                offering_name: {
                  query: searchTerm,
                  operator: 'AND',
                  boost: 5
                }
              }
            },
            {
              match: {
                offering_name: {
                  query: searchTerm,
                  fuzziness: 'AUTO',
                  operator: 'AND',
                  prefix_length: 2
                }
              }
            },
            {
              match: {
                sponsor_name: {
                  query: searchTerm,
                  fuzziness: 'AUTO',
                  operator: 'AND',
                  prefix_length: 2
                }
              }
            },
            {
              match: {
                formatted_place: {
                  query: searchTerm,
                  fuzziness: 'AUTO',
                  operator: 'AND',
                  prefix_length: 2
                }
              }
            }
          ]
        }
      }
    : {};
};

export const buildSearchFilter = (filters?: IFilter[], locationFilter?: ILocationFilter) => {
  const body = filters?.length
    ? (filters
        .filter((filter) =>
          [
            'sponsor_id',
            'asset_type',
            'offering_status',
            'offering_type',
            't12_yield',
            'implied_cap_rate'
          ].includes(<string>filter.field)
        )
        .filter((filter) => filter?.value?.length)
        .map((filter) => {
          return buildSearchTermFilter(filter);
        }, [])
        .filter((filter) => filter)
        .flat(1) as unknown[])
    : [];

  if (locationFilter?.field && locationFilter?.value) {
    body.push(buildLocationQuery(locationFilter?.field, locationFilter?.value));
  }

  return body?.length ? { bool: { must: body } } : {};
};

const buildSearchTermFilter = (filter: IFilter) => {
  switch (filter.field) {
    case 'asset_type':
      if (!filter?.value?.length) {
        return;
      }
      return buildSearchMultiQuery(filter.field, filter?.value, false);

    case 'offering_status':
    case 'offering_type':
      if (!filter?.value?.length) {
        return;
      }
      return buildSearchMultiQuery(filter.field, filter?.value, false);

    case 't12_yield':
    case 'implied_cap_rate':
      if (!filter?.value?.length) {
        return;
      }
      return buildSearchMultiQuery(filter.field, filter?.value, true);

    default:
      if (!filter?.value?.length) {
        return;
      }

      return { terms: { [`${filter.field}`]: filter?.value } };
  }
};

const buildLocationQuery = (field: string, value: Envelope) => {
  return {
    geo_bounding_box: {
      [`${field}`]: {
        top_left: {
          lat: value.tlLat,
          lon: value.tlLon
        },
        bottom_right: {
          lat: value.brLat,
          lon: value.brLon
        }
      }
    }
  };
};

const buildSearchMultiQuery = (field: string, value: string[], isRange: boolean) => {
  return {
    bool: {
      minimum_should_match: 1,
      should: isRange ? buildSearchRangeQuery(field, value) : [{ terms: { [field]: value } }]
    }
  };
};

const formatFilterRange = (filterValue: string) => {
  const filterParts = filterValue.split('-');
  if (!filterParts.length) {
    return {};
  }

  if (filterParts.length == 1) {
    return filterParts[0] ? { gte: filterParts[0] } : {};
  }

  if (filterParts[0] == '*' && filterParts[1]) {
    return { lt: filterParts[1] };
  }

  if (filterParts[0] && filterParts[1] == '*') {
    return { gte: filterParts[0] };
  }

  if (filterParts[0] && filterParts[1]) {
    return { gte: filterParts[0], lt: filterParts[1] };
  }

  return {};
};

const buildSearchRangeQuery = (filterField: string, filterValues?: string[]) => {
  return (
    filterValues &&
    filterValues
      .filter((value) => value && value.split('-').length == 2)
      .map((value: string) => {
        const formattedValue = formatFilterRange(value);
        return { range: { [filterField]: [formattedValue] } };
      })
  );
};

export const removeSingleFilterValue = (
  filters: IFilter[],
  removeField: string,
  removeValue: string
) => {
  return filters.reduce((prev, filter) => {
    const filterField = filter.field;
    const filterValue = filter?.value;

    if (filterField === removeField) {
      const updatedFilterValue =
        filterValue &&
        filterValue.filter((value) => {
          return value !== removeValue;
        });

      if (updatedFilterValue && updatedFilterValue.length > 0) {
        return [
          ...prev,
          {
            field: filterField,
            value: updatedFilterValue
          }
        ];
      } else {
        return prev;
      }
    }

    return [...prev, filter] as IFilter[];
  }, [] as IFilter[]);
};

export const filtersFromUrlQuery = (query: ParsedUrlQuery) => {
  if (!query) return;

  const filters = Object.entries(query)
    .filter(([key, value]) => key && value !== undefined && !['t', 'g', 's', 'sd'].includes(key))
    .map(([key, value]) => {
      return { field: key, value: Array.isArray(value) ? value : ([value] as string[]) };
    });

  const offeringTerm = getSingleValue(query?.t);
  const userTerm = getSingleValue(query?.ut);
  const sortField = getSingleValue(query?.s);
  const sortDesc = Boolean(getSingleValue(query?.sd));

  return { offeringTerm, userTerm, filters, sortField, sortDesc };
};

export const filtersToUrlQuery = (params: QueryParams) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let entries: any[] = [];

  if (params?.filters && !isEmpty(params?.filters)) {
    entries =
      params?.filters
        ?.filter((filter) => !['t', 'ut', 's', 'sd', 'all'].includes(filter.field))
        ?.map((filter) => [filter.field, filter.value]) || [];
  }

  if (params?.term) entries.push(['t', params?.term]);
  if (params?.userTerm) entries.push(['ut', params?.userTerm]);
  if (params?.sortField) entries.push(['s', params?.sortField]);
  if (params?.sortDesc) entries.push(['sd', params?.sortDesc]);

  return Object.fromEntries(entries);
};

export const useUserTermUrlParam = (callback: (value?: string) => void) => {
  const router = useRouter();
  useEffect(() => {
    const searchTerm = getSingleValue(router?.query?.ut);
    callback(searchTerm as string | undefined);
  }, [router.isReady, router?.query?.ut, callback]);
};
